import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { ActionMenuDataType } from "@emisgroup/cohort-builder-kit";
import {
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuContent, DropdownMenuItem
} from "@emisgroup/ui-dropdown-menu";
import { Button } from "@emisgroup/ui-button";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { ProgressSpinnerSlice } from "../../../../ProgressSpinner/Index";
import { BandsTypes } from "../../Banding/Types";
import { OutputConfigRemove } from "../../OutputConfigLandingPage/OutputConfigList/RemoveOutputConfig.Logic";
import { DeleteTypes } from "../../OutputConfigLandingPage/OutputConfigList/Constants";
import { OutputMappingChild } from "../OutputMappingList/Types";
import {
  OutputMappingChildCardsForBandingsView
} from "../OutputMappingChildCardsForBandings/OutputMappingChildCardsForBandings.view";
import {
  Checkbox,
  FormElement
} from "../../../../Common/UI/Index";
import { removeHighlight } from "../../helper";
import { OUTPUT_CONFIG_MAP_HIGHLIGHT } from "../../Constant";
import TriangleDown from "~icons/ic/outline-arrow-drop-down";
import Edit from "~icons/ic/outline-edit";
import "./Styles.scss";

interface Props {
  /**
   * Child item details
   */
  mappingChildItem: OutputMappingChild;
  /**
   * Layer id in render order
   */
  layerId: number;
  /**
   * On Toggle select
   */
  onToggleButtonSelect: (value: number) => void;
  /**
   * Selected Toggle
   */
  selectedToggle: number;
  /**
   * On Manage Bandings Dropdown select
   */
  onManageBandingsClick: (band: string) => void;
  /**
   * Action when edit button is clicked
   */
  onEditConfigDetail: () => void;
  /**
   * Pass band type to parent
   * @param value Band type
   */
  passToParent: (value: string) => void;
  /**
   * Pass bandtype to prev child
   * @param value Band type
   */
  passToPrev: (value: string) => void;
}

/**
 * Responsible to render Output Mapping Child Card view
 * @returns Output Mapping Child Card view
 */
export const OutputMappingChildCardView: React.FC<Props> = (props: Props) => {
  const {
    mappingChildItem,
    layerId,
    onToggleButtonSelect,
    selectedToggle,
    onManageBandingsClick,
    onEditConfigDetail,
    passToParent,
    passToPrev
  } = props;
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [splitDropdownMenuOpen, setSplitDropdownMenuOpen] = useState(false);
  const { tag } = mappingChildItem;
  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const { focusConfigId } = outputConfig;
  /**
   * Method to close the dropdown menu
   */
  const dropdownAwayClick = (isOpen: boolean): void => {
    setSplitDropdownMenuOpen(isOpen);
  };

  /**
   * Hide dropdown on scroll
   */
  const handleScroll = (): void => {
    setSplitDropdownMenuOpen(false);
  };


  /**
   * Dropdown content
   */
  const drpDwnData: ActionMenuDataType[] = [
    {
      id: "1",
      name: BandsTypes.AGE,
      onClick: (): void => onManageBandingsClick(BandsTypes.AGE)
    },
    {
      id: "2",
      name: BandsTypes.GENDER,
      onClick: (): void => onManageBandingsClick(BandsTypes.GENDER)
    },
    {
      id: "3",
      name: BandsTypes.ETHNICITY,
      onClick: (): void => onManageBandingsClick(BandsTypes.ETHNICITY)
    },
  ];

  /**
   * Action to scroll when final output id is found
   */
  const handleScrollIntoView = async (): Promise<void> => {
    const elementClassName = "ebt-outputConfig-config-";

    const element = document.getElementById(elementClassName + mappingChildItem.configMapId) ||
      document.getElementById(elementClassName + mappingChildItem.parentMapId);
    if (element) {
      passToParent(mappingChildItem.bandType);
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.FindingConfig")));
      await new Promise((resolve) => setTimeout(resolve, 3000));

      element.classList.add(OUTPUT_CONFIG_MAP_HIGHLIGHT);
      // 👇 Will scroll smoothly to the top of the next section
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      removeHighlight(element, OUTPUT_CONFIG_MAP_HIGHLIGHT, dispatch);
    }
  };
  /**
   * Register scroll event 
   */
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);
    if (focusConfigId) {
      if (mappingChildItem.configMapId === focusConfigId) {
        handleScrollIntoView();
      }
    }

    // Remove the event listener
    return (): void => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  return (
    <div
      className={`${layerId === 1 ? "first-child-item" : "second-child-item"}`}
      id={"ebt-outputConfig-config-" + mappingChildItem.configMapId}
    >
      <div
        className="grid-container-child"
        id="ebt-outputConfig-config-childCard-container"
      >
        <div
          className="grid-container-child__metadata"
          id="ebt-outputConfig-config-childCard-metadata"
        >
          <div className="ebt-mapping-container"
            id="ebt-outputConfig-config-childCard"
          >
            <strong className="ebt-mapping-output">{translate("OutputConfig.OutputMapping.Mapping")}</strong>
            <div className="ebt-mapping-details" title={tag} id="ebt-outputConfig-config-childCard-tag">{tag}</div>
          </div>
        </div>
        <div className="ebt-outputConfig-grid-child__actions">
          <div>
            <FormElement
              id="ebt-outputConfig-config-childCard-includeInOutput"
              fieldId="childIncludeInOutput"
            >
              <Checkbox
                className="ebt-mapping__include-in-xml"
                id="ebt-outputConfig-config-includexml-child"
                labelText={translate("OutputConfig.OutputMappingAgeForm.IncludeInOutput")}
                name="childIncludeInOutput"
                checked={mappingChildItem.includeInOutput}
                disabled={true}
              />
            </FormElement>
          </div>
          <div
            className="output-config__related_parent"
            id="ebt-outputConfig-config-parent"
          >
            <DropdownMenu
              onOpenChange={(data): void => dropdownAwayClick(data)}
              open={splitDropdownMenuOpen}
            >
              <DropdownMenuTrigger
                className="ebt-opcard-trigger">
                <Button className="ebt-opcard-bandbtn">
                  {translate("OutputConfig.OutputMappingButtons.ManageBandings")}
                  <TriangleDown title="Expand" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="ebt-op-child-content"
                data-testid="ebt-op-child-content"
              >
                {drpDwnData.map((menu: ActionMenuDataType) => {
                  return (
                    <DropdownMenuItem
                      key={menu.id}
                      onClick={menu?.onClick}
                    >

                      {menu.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="ebt-outputConfig-grid-child__actions-container">
            <div className="ebt-output-config__list__delete-icon">
              <OutputConfigRemove
                deleteType={DeleteTypes.DELETE_OUTPUT_MAP_LIST}
                mapId={mappingChildItem.configMapId}
                mapName={mappingChildItem.configName}
              />
            </div>
            <Edit
              title="edit"
              className="ebt-output-config__list__edit-icon"
              onClick={onEditConfigDetail}
              id="ebt-outputConfig-config-edit"
            />
          </div>
        </div>
      </div>
      {
        mappingChildItem &&
        mappingChildItem.childrenOutputElements &&
        mappingChildItem.childrenOutputElements?.length > 0 &&
        <OutputMappingChildCardsForBandingsView
          mappingChildItem={mappingChildItem}
          layerId={2}
          onToggleButtonSelect={onToggleButtonSelect}
          selectedToggle={selectedToggle}
          show={!!focusConfigId}
          passToPrev={passToPrev}
        />
      }
    </div>
  );
};
